import { useEffect, useRef } from "react";

/**
 * Calls back the once, when a list the reader was working through runs out
 * under them.
 *
 * The distinction it exists to draw is between clearing a list and arriving at
 * one that was already empty. Only the first is an achievement, and a
 * celebration fired on the second would go off every time someone with nothing
 * to do opened the screen. So it remembers having seen rows, and fires on the
 * edge rather than on the state.
 *
 * Shared because both platforms owe the reader the same moment, and because the
 * rule is the easy thing to get subtly wrong in two places: a first version
 * fired whenever the list was empty rather than when it had just emptied.
 *
 * `enabled` is read first and resets the memory, so a reader who switches
 * filter to an empty one is not congratulated for a list they never worked —
 * and switching back does not fire either, until they clear it themselves.
 */
export function useListCleared({
  remaining,
  enabled = true,
  onCleared,
}: {
  /** How many rows the reader still has in front of them. */
  remaining: number;
  /** Whether this list is one worth marking the end of at all. */
  enabled?: boolean;
  onCleared: () => void;
}) {
  const hadRows = useRef(false);
  // Held so the effect is driven by the count rather than re-running whenever a
  // caller passes a fresh arrow, which would re-check the edge mid-celebration.
  const latest = useRef(onCleared);
  useEffect(() => {
    latest.current = onCleared;
  });

  useEffect(() => {
    if (!enabled) {
      hadRows.current = false;
      return;
    }
    if (remaining > 0) {
      hadRows.current = true;
      return;
    }
    if (!hadRows.current) return;
    hadRows.current = false;
    latest.current();
  }, [remaining, enabled]);
}
